import React, { useState } from "react";
import { Link } from "@tanstack/react-router";
import useApiClient from "../../hooks/useApiClient";
import { MINIMUM_SEARCH_TOKEN_LENGTH } from "@/utils/searchQueryValidation";
import PaginationFooter from "../paginationFooter";
import prettyBytes from "pretty-bytes";
import DataAssetCard from "../cards/dataAssetCard";
import SortDropdown from "../dropdowns/sortDropdown";
import FilterChipList from "../filterChip/filterChipList";
import GeneralFiltersGroup from "../filterGroups/generalFiltersGroup";
import RealAssetFiltersGroup from "../filterGroups/realAssetFiltersGroup";
import SealogEventFiltersGroup from "../filterGroups/sealogEventFiltersGroup";
import AdvancedFiltersGroup from "../filterGroups/advancedFiltersGroup";
import SpecifySearchTypeDropdown from "../dropdowns/specifySearchTypeDropdown";
import FilterChip from "../filterChip";
import SmartFilterChip from "../filterChip/smartFilterChip";
import { FaSearch } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import useDataAssetList from "../../hooks/useDataAssetList";
import LoadingDataAssetCards from "../loaders/loadingDataAssetCards";
import Skeleton from "../loaders/Skeleton";
import DataAssetPreviewDialog from "./dataAssetPreviewDialog";
import DataSetSaveModal from "../dataSet/dataSetSaveModal";

const BaseDataAssetList = ({
  shouldGrabFocus = true,
  title,
  excludedFilters = [] as string[],
  excludeFromClear = [] as string[],
  filterConfigs = {} as Record<string, any>,
  className = "",
  maxWidth = "",
  enableDataSetSave = false,
}: {
  shouldGrabFocus?: boolean;
  title: any;
  excludedFilters?: string[];
  excludeFromClear?: string[];
  filterConfigs?: Record<string, any>;
  className?: string;
  maxWidth?: string;
  enableDataSetSave?: boolean;
}) => {
  const api = useApiClient();

  const {
    rawValues,
    setRawValues,
    queryParams,
    inputRef,
    handleRealAssetSelection,
    handleDeploymentSelection,
    handleManifestSelection,
    handleDataSetSelection,
    handleSealogEventValueSelection,
    handleSealogEventFreeTextSelection,
    handleSealogEventOptionNameSelection,
    handleSealogEventOptionValueSelection,
    handleFileTypesSelection,
    handleSortChange,
    handleClearAll,
    handleRegexToggleChange,
    handleDateValueChange,
    specifiedSearchType,
    handleSpecifiedSearchTypeChange,
    hasFilterChipActive,
    hasSearchParams,
    searchIsRunnable,
    searchQueryTooShort,
    isLoading,
    data,
    isPreviousData,
    searchStatsAreLoading,
    searchStatsData,
    setDebouncedQueryParams,
    previewNavigator,
  } = useDataAssetList(shouldGrabFocus, excludeFromClear, excludedFilters);

  const [areAdvancedFiltersToggled, setAreAdvancedFiltersToggled] =
    useState(false);
  const [areSealogFiltersToggled, setAreSealogFiltersToggled] = useState(false);
  const [isDataSetSaveModalOpen, setIsDataSetSaveModalOpen] = useState(false);
  const [saveSuccessState, setSaveSuccessState] = useState<{
    action: "create" | "update";
    dataSetName: string;
    publicId: string;
  } | null>(null);
  const showDataSetSaveButton =
    enableDataSetSave && (searchStatsData?.number_of_assets ?? 0) > 0;

  const toggleSealogFilters = () => setAreSealogFiltersToggled((prev) => !prev);
  const toggleAdvancedFilters = () =>
    setAreAdvancedFiltersToggled((prev) => !prev);

  const initialSortOrder = queryParams.sortOrder;

  const filterChips = [
    !excludedFilters.includes("fileType") && {
      title: "File Type",
      items: rawValues.fileTypes,
      maxVisible: 3,
      onClose: handleFileTypesSelection,
    },
    !excludedFilters.includes("realAsset") && {
      title: "Real Assets",
      items: rawValues.realAssetPublicIds,
      maxVisible: 4,
      onClose: handleRealAssetSelection,
      renderChip: (publicId: string) => (
        <SmartFilterChip
          key={publicId}
          title="Real Asset"
          selection={publicId}
          queryKey={["realAssetDetail", publicId]}
          onClose={() => handleRealAssetSelection(publicId)}
          mapQueryResponseToFilterChipLabel={(response: any) =>
            response?.name ?? ""
          }
          queryFn={() => api.dataAccess.realAssetDetail({ publicId })}
        />
      ),
    },
    !excludedFilters.includes("deployment") && {
      title: "Real Asset Deployments",
      items: rawValues.realAssetDeploymentPublicIds,
      maxVisible: 4,
      onClose: handleDeploymentSelection,
      renderChip: (publicId: string) => (
        <SmartFilterChip
          key={publicId}
          title="Deployment"
          selection={publicId}
          queryKey={["realAssetDeploymentDetail", publicId]}
          onClose={() => handleDeploymentSelection(publicId)}
          mapQueryResponseToFilterChipLabel={(response: any) =>
            response?.name ?? ""
          }
          queryFn={() => api.dataAccess.realAssetDeploymentDetail({ publicId })}
        />
      ),
    },
    !excludedFilters.includes("eventTypes") && {
      title: "Event Types",
      items: rawValues.sealogEventValues,
      maxVisible: 4,
      onClose: handleSealogEventValueSelection,
    },
    !excludedFilters.includes("eventDescriptions") && {
      title: "Event Descriptions",
      items: rawValues.sealogEventFreeTexts,
      maxVisible: 4,
      onClose: handleSealogEventFreeTextSelection,
    },
    !excludedFilters.includes("eventAdditionalTypes") && {
      title: "Event Additional Types",
      items: rawValues.sealogEventOptionNames,
      maxVisible: 2,
      onClose: handleSealogEventOptionNameSelection,
    },
    !excludedFilters.includes("eventAdditionalDescriptions") && {
      title: "Event Additional Descriptions",
      items: rawValues.sealogEventOptionValues,
      maxVisible: 2,
      onClose: handleSealogEventOptionValueSelection,
    },
    !excludedFilters.includes("dataSet") && {
      title: "Data Sets",
      items: rawValues.dataSetPublicIds,
      maxVisible: 2,
      onClose: handleDataSetSelection,
      renderChip: (publicId: string) => (
        <SmartFilterChip
          key={publicId}
          title="Data Set"
          selection={publicId}
          queryKey={["dataSetDetail", publicId]}
          onClose={() => handleDataSetSelection(publicId)}
          mapQueryResponseToFilterChipLabel={(response: any) =>
            response?.name ?? ""
          }
          queryFn={() => api.dataAccess.dataSetDetail({ publicId })}
        />
      ),
    },
    !excludedFilters.includes("manifest") && {
      title: "Manifests",
      items: rawValues.manifestPublicIds,
      maxVisible: 2,
      onClose: handleManifestSelection,
      renderChip: (publicId: string) => (
        <SmartFilterChip
          key={publicId}
          title="Manifest"
          selection={publicId}
          queryKey={["manifestMetadata", publicId]}
          onClose={() => handleManifestSelection(publicId)}
          mapQueryResponseToFilterChipLabel={(response: any) =>
            response?.name ?? ""
          }
          queryFn={() => api.dataTransfer.getManifestMetadata({ publicId })}
        />
      ),
    },
  ].filter(Boolean) as Array<{
    title: string;
    items: string[];
    maxVisible: number;
    onClose: (selection: unknown) => void;
    renderChip?: (publicId: string) => React.ReactNode;
  }>;

  return (
    <div className={`flex flex-col items-center ${className}`}>
      <DataAssetPreviewDialog
        isOpen={previewNavigator.isPreviewOpen}
        dataAsset={previewNavigator.selectedAsset}
        initialPreviewUrl={previewNavigator.initialPreviewUrl}
        onClose={previewNavigator.closePreview}
        onPrevious={previewNavigator.goToPreviousPreview}
        onNext={previewNavigator.goToNextPreview}
        hasPrevious={previewNavigator.hasPrevious}
        hasNext={previewNavigator.hasNext}
        navigatingDirection={previewNavigator.navigatingDirection}
        previousUnavailableMessage={previewNavigator.previousUnavailableMessage}
        nextUnavailableMessage={previewNavigator.nextUnavailableMessage}
        sealogSearchTerm={queryParams.sealogEventQuery ?? ""}
        sealogEventValues={queryParams.sealogEventValues}
        sealogEventFreeTexts={queryParams.sealogEventFreeTexts}
        sealogEventOptionNames={queryParams.sealogEventOptionNames}
        sealogEventOptionValues={queryParams.sealogEventOptionValues}
        regex={queryParams.regex}
      />
      {enableDataSetSave && searchStatsData && (
        <DataSetSaveModal
          isOpen={isDataSetSaveModalOpen}
          onClose={() => setIsDataSetSaveModalOpen(false)}
          onSuccess={setSaveSuccessState}
          resultCount={searchStatsData.number_of_assets}
          totalDataVolume={searchStatsData.total_data_volume}
          searchParams={queryParams as unknown as Record<string, unknown>}
        />
      )}
      <div className={`w-full ${maxWidth}`}>
        {title && <h1 className="mb-3 text-2xl text-blue-200">{title}</h1>}
        <div className="grid grid-cols-12 rounded-md border-2 border-solid p-2">
          {/* Search Input */}
          <div className="col-span-8 col-start-3 mb-[0.875rem]">
            <div className="relative flex h-full items-center">
              <FaSearch
                className="absolute left-3 top-10 z-10 text-gray-400"
                size={20}
              />
              <label className="relative flex-grow">
                <p className="font-light text-blue-400">
                  {specifiedSearchType === "Files"
                    ? "Search for: OXR2021, JPG, PNG, JSON, and more!"
                    : "Search for: Coral, Fish, Substrate, Samples and more!"}
                </p>
                <input
                  ref={inputRef}
                  onChange={(e) => {
                    e.preventDefault();
                    if (specifiedSearchType === "Files") {
                      const query = e.target.value;
                      setRawValues({ ...rawValues, query });
                      setDebouncedQueryParams({
                        query,
                        page: 1,
                      });
                    } else {
                      const sealogEventQuery = e.target.value;
                      setRawValues({ ...rawValues, sealogEventQuery });
                      setDebouncedQueryParams({
                        sealogEventQuery,
                        page: 1,
                      });
                    }
                  }}
                  value={
                    specifiedSearchType === "Files"
                      ? rawValues.query
                      : rawValues.sealogEventQuery
                  }
                  type="text"
                  className="form-input block min-h-[3rem] w-full rounded-md py-2 pl-10 pr-56 text-blue-300 hover:shadow-md focus:shadow-md focus:outline-none focus:ring-0 focus-visible:outline-none"
                  placeholder={
                    specifiedSearchType === "Files"
                      ? "Search in File Paths and Extensions"
                      : "Search in Sealog Export Contents"
                  }
                />
                {rawValues.query && (
                  <button
                    aria-label="clear input"
                    onClick={() => {
                      setRawValues({ ...rawValues, query: "" });
                      setDebouncedQueryParams({ query: "", page: 1 });
                    }}
                    className="absolute right-48 top-[3rem] -translate-y-1/2 transform text-gray-400 hover:text-gray-600"
                    style={{ outline: "none" }}
                  >
                    <IoClose size={28} />
                  </button>
                )}
                {rawValues.sealogEventQuery && (
                  <button
                    onClick={() => {
                      setRawValues({ ...rawValues, sealogEventQuery: "" });
                      setDebouncedQueryParams({
                        sealogEventQuery: "",
                        page: 1,
                      });
                    }}
                    className="absolute right-48 top-[3rem] -translate-y-1/2 transform text-gray-400 hover:text-gray-600"
                    style={{ outline: "none" }}
                  >
                    <IoClose size={28} />
                  </button>
                )}
                {specifiedSearchType === "Files" && searchQueryTooShort && (
                  <p
                    role="status"
                    className="mt-1 text-sm font-light text-gray-500"
                  >
                    Keep typing — each search term needs at least{" "}
                    {MINIMUM_SEARCH_TOKEN_LENGTH} characters.
                  </p>
                )}
              </label>
              <div className="absolute right-2 top-12 z-50 -translate-y-1/2 transform">
                <SpecifySearchTypeDropdown
                  onChange={
                    handleSpecifiedSearchTypeChange as (value: string) => void
                  }
                  initialValue={specifiedSearchType}
                  buttonStyle={"py-2"}
                />
              </div>
            </div>
          </div>

          {/* Filter Groups */}
          <div className="col-span-10 col-start-2 mb-[1rem] flex flex-wrap justify-between gap-x-4 gap-y-4">
            {!excludedFilters.includes("general") && (
              <GeneralFiltersGroup
                rawValues={rawValues}
                handleDateValueChange={handleDateValueChange}
                handleFileTypesSelection={handleFileTypesSelection}
                showDateFilter={true}
                showFileTypeFilter={true}
                {...filterConfigs.general}
              />
            )}
            {!excludedFilters.includes("realAsset") && (
              <RealAssetFiltersGroup
                rawValues={rawValues}
                handleRealAssetSelection={handleRealAssetSelection}
                handleDeploymentSelection={handleDeploymentSelection}
                showRealAssetFilter={true}
                showDeploymentFilter={true}
                {...filterConfigs.realAsset}
              />
            )}
          </div>

          {!excludedFilters.includes("sealog") && (
            <SealogEventFiltersGroup
              rawValues={rawValues}
              handleSealogEventValueSelection={handleSealogEventValueSelection}
              handleSealogEventFreeTextSelection={
                handleSealogEventFreeTextSelection
              }
              handleSealogEventOptionNameSelection={
                handleSealogEventOptionNameSelection
              }
              handleSealogEventOptionValueSelection={
                handleSealogEventOptionValueSelection
              }
              areSealogFiltersToggled={areSealogFiltersToggled}
              toggleSealogFilters={toggleSealogFilters}
              showEventValueFilter={true}
              showEventFreeTextFilter={true}
              showEventOptionNameFilter={true}
              showEventOptionValueFilter={true}
              {...filterConfigs.sealog}
            />
          )}

          <hr className="col-span-10 col-start-2 mr-[0.15rem] mt-3 border-gray-300" />

          {!excludedFilters.includes("advanced") && (
            <AdvancedFiltersGroup
              rawValues={rawValues}
              queryParams={queryParams}
              handleManifestSelection={handleManifestSelection}
              handleDataSetSelection={handleDataSetSelection}
              handleRegexToggleChange={handleRegexToggleChange}
              areAdvancedFiltersToggled={areAdvancedFiltersToggled}
              toggleAdvancedFilters={toggleAdvancedFilters}
              showManifestFilter={!excludedFilters.includes("manifest")}
              showDataSetFilter={!excludedFilters.includes("dataSet")}
              showRegexFilter={!excludedFilters.includes("regex")}
              {...filterConfigs.advanced}
            />
          )}
        </div>
      </div>

      {/* Results Section */}
      <div className={`w-full ${maxWidth}`}>
        {!searchIsRunnable ? (
          <></>
        ) : (
          <>
            <div className="mt-5 space-y-4">
              {saveSuccessState && (
                <div
                  aria-live="polite"
                  className="rounded-lg border border-blue-50 bg-blue-50/40 px-4 py-3 text-sm text-blue-300 shadow-sm"
                >
                  <p className="font-semibold">
                    {saveSuccessState.action === "create"
                      ? `Saved this search as "${saveSuccessState.dataSetName}".`
                      : `Added these search results to "${saveSuccessState.dataSetName}".`}
                  </p>
                  <p className="mt-1 text-grey-200">
                    You are still on the search page.{" "}
                    <Link
                      className="font-semibold text-blue-100 underline"
                      to={`/data/sets/${saveSuccessState.publicId}` as any}
                    >
                      Open the data set detail page
                    </Link>
                    .
                  </p>
                </div>
              )}
              <div className="flex justify-between">
                <div className="flex flex-col">
                  <p className="text-3xl text-blue-200">Results</p>
                  <div className="ml-[2px] mt-1 text-sm text-grey-200">
                    {hasSearchParams &&
                      (searchStatsAreLoading ? (
                        <Skeleton className="mb-3 h-4 w-48" />
                      ) : searchStatsData ? (
                        <p>
                          Number of Results:{" "}
                          {searchStatsData.number_of_assets.toLocaleString()} |
                          Total File Size:{" "}
                          {prettyBytes(searchStatsData.total_data_volume)}
                        </p>
                      ) : null)}
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  {showDataSetSaveButton && (
                    <button
                      className="ox-button !mt-0 rounded-md px-3 py-2 text-sm"
                      onClick={() => setIsDataSetSaveModalOpen(true)}
                      type="button"
                    >
                      Save as Data Set
                    </button>
                  )}
                  <SortDropdown
                    label="Asset Created"
                    onChange={handleSortChange}
                    initialSortOrder={initialSortOrder}
                  />
                </div>
              </div>

              <hr className="my-6 border-t-2 border-gray-300" />
              <div className="mb-8 flex flex-wrap gap-2">
                <FilterChipList filters={filterChips} />
                {hasFilterChipActive && (
                  <FilterChip
                    key="clear-all"
                    label="Clear All Filters"
                    onClose={handleClearAll}
                  />
                )}
              </div>
              {hasSearchParams &&
                (isLoading ? (
                  <div className="mt-[7rem]">
                    <LoadingDataAssetCards size={queryParams.size} />
                  </div>
                ) : (data?.items?.length ?? 0) > 0 ? (
                  data?.items.map((dataAsset, i) => (
                    <DataAssetCard
                      dataAsset={dataAsset}
                      key={`${i}-${dataAsset.path}`}
                      isJustOpened={
                        previewNavigator.lastOpenedAssetId ===
                        (dataAsset.public_id || dataAsset.path)
                      }
                      onPreviewOpen={previewNavigator.openPreview}
                      sealogSearchTerm={queryParams.sealogEventQuery ?? ""}
                      sealogEventValues={queryParams.sealogEventValues}
                      sealogEventFreeTexts={queryParams.sealogEventFreeTexts}
                      sealogEventOptionNames={
                        queryParams.sealogEventOptionNames
                      }
                      sealogEventOptionValues={
                        queryParams.sealogEventOptionValues
                      }
                      regex={queryParams.regex}
                    />
                  ))
                ) : (
                  <p>No results found.</p>
                ))}
            </div>

            <PaginationFooter
              isPreviousData={isPreviousData}
              page={rawValues.page}
              setPage={(page: number) => setRawValues({ page })}
              hasMore={!!(data && data.items.length !== 0)}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default BaseDataAssetList;
